/* eslint-disable no-undef */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { enqueueSnackbar } from 'notistack';
import { useHistory, useLocation } from 'react-router-dom';
import {
  IconCheck,
  IconZap,
  IconSparkles,
  IconArrowRight,
  IconShieldCheck,
  IconMinus,
  IconPlus,
  IconShoppingCart,
  IconX,
  IconCalculator,
  IconDatabase,
  IconTarget,
  IconRotateCcw,
  IconAlertTriangle,
} from './SVGIcons';
import CreditCalculator, {
  databaseCreditPacks,
  productDatabaseCreditPacks,
  analyzersCreditPacks,
  workflowsCreditPacks,
  initialCreditSelections,
  getCreditCalcTotals,
  databaseCreditPackPriceIds,
  databaseCombinedCreditPackPriceIds,
  productDatabaseCreditPackPriceIds,
  analyzersCreditPackPriceIds,
  workflowsCreditPackPriceIds,
  formatCreditPackPrice,
  useCreditPackCatalog,
  type CreditSelections,
} from './CreditCalculator';
import CheckoutModal from './CheckoutModal';
import CreditFaq from './CreditFaq';
import PaymentsAPI from 'api/PaymentsAPI';
import CreditsAPI from 'api/CreditsAPI';
import LicensesAPI from 'api/LicensesAPI';
import { GlobalContext } from 'providers/GlobalProvider';
import { resolveLicenseTypeForItem } from 'utils/licenseInventory';
import { useActiveLicenseManager } from './useActiveLicenseManager';
import {
  ConfirmModal,
  CancelRenewModal,
  getNetProrationAmount,
} from './PlanConfirmModals';

interface CheckoutSubscriptionItem {
  priceId: string;
  quantity: number;
  moduleName: string;
  type:
    | 'fixed'
    | 'database_credit'
    | 'product_database_credit'
    | 'analyzers_credit'
    | 'workflows_credit'
    | 'business_verification_addon'
    | 'corporate_ownership_add_on'
    // Tier change on an already-active recurring pack (Database or
    // Workflows) -- CheckoutModal calls PaymentsAPI.changePlan for these
    // instead of creating a new subscription.
    | 'database_credit_change'
    | 'workflows_credit_change'
    // Merges Database's two legacy subscriptions (Entity DB + Product DB)
    // into one at the chosen tier -- CheckoutModal calls
    // PaymentsAPI.mergeDatabaseCreditSubscriptions for this.
    | 'database_credit_merge';
  includedAnalysisCreditsMode?: 'one_time';
  // Only set for the *_change/*_merge types above.
  subId?: string;
  subItemId?: string;
  secondarySubId?: string;
  // Set on the one licence line whose seat the buyer should take; the
  // invoice.paid webhook assigns it once the purchase is paid.
  autoAssignLicense?: 'standard' | 'pro' | 'business';
}

// Subscription Prices
const standardPrice = process.env.REACT_APP_STANDARD_PRICE || '';
const proPrice = process.env.REACT_APP_PRO_PRICE || '';
const businessPrice = process.env.REACT_APP_BUSINESS_PRICE || '';

const databaseCreditPriceIds = databaseCreditPackPriceIds;
const databaseCombinedCreditPriceIds = databaseCombinedCreditPackPriceIds;
const productDatabaseCreditPriceIds = productDatabaseCreditPackPriceIds;
const analyzersCreditPriceIds = analyzersCreditPackPriceIds;
const workflowsCreditPriceIds = workflowsCreditPackPriceIds;

const plans = [
  {
    id: 'standard',
    name: 'Standard',
    price: 5,
    description: 'Look up tariffs instantly',
    badge: null,
    accent: 'var(--plan-starter)',
    capabilities: [
      'Tariff & duty lookup for individual products',
      'Country-specific duty visibility',
      'Customs Flags to identify potential risk',
    ],
    outcomes: [
      'Check duties before importing',
      'Validate landed costs quickly',
    ],
    cta: 'Add Standard',
    note: 'Explore the core standard compliance tools',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 64,
    description: 'Analyze your trade data at scale',
    badge: 'Most Popular',
    accent: 'var(--plan-pro)',
    capabilities: [
      'Bulk tariff simulations across product datasets',
      'ACE (ES003) report analysis',
      'Visualize duty spend patterns and anomalies',
    ],
    outcomes: [
      'Model sourcing scenarios',
      'Replace manual Excel analysis',
      'Surface compliance risks',
    ],
    cta: 'Add Pro',
    note: 'Most popular for teams looking to automate compliance',
  },
  {
    id: 'business',
    name: 'Business',
    price: 99,
    description: 'Run and manage compliance workflows',
    badge: 'Best for Teams',
    accent: 'var(--plan-business)',
    capabilities: [
      'Guided import & export workflows',
      'BITE DB — continuous monitoring of products and entities',
      'Alerts, task tracking, and workflow management',
    ],
    outcomes: [
      'Standardize compliance processes',
      'Stay continuously up to date with your Product Catalog',
      'Manage compliance across teams for Bulk DPS screening',
    ],
    cta: 'Add Business',
    note: 'Everything you need plus premium support and reliability',
  },
];

const enterprisePlan = {
  id: 'enterprise',
  name: 'Enterprise',
  description: 'Embed compliance across your systems and workflows',
  badge: 'Custom',
  accent: 'var(--plan-enterprise)',
  capabilities: [
    'ERP and TMS integrations',
    'Data enrichment pipelines',
    'Custom workflows and implementations',
    'Business Verification',
    'Corporate Ownership',
  ],
  outcomes: [
    'Sync compliance across systems',
    'Eliminate manual processes',
    'Scale globally',
  ],
};

// Optional Database add-ons. Each is its own Stripe subscription; the webhook
// turns the matching Keycloak role on/off from it.
// Both are usage-based: a metered price with no quantity and no upfront
// charge, available only with an active Database credit package.
//  - Business Verification reports each entity verification to Stripe's meter after
//    the check runs; it lands on the monthly invoice at `perEntity`.
//  - Corporate Ownership uses the same purchase flow, but its usage is not
//    metered yet, so nothing is charged for it until that is wired in.
const databaseAddOns = [
  {
    id: 'businessVerification',
    label: 'Add on Business Verification',
    perCredit: 0,
    perEntity: 0.5,
    usageBased: true,
    type: 'business_verification_addon',
    moduleName: 'Business Verification Add-on',
    priceId: process.env.REACT_APP_BUSINESS_VERIFICATION_ADDON_PRICE || '',
  },
  {
    id: 'corporateOwnership',
    label: 'Add on Corporate Ownership Check',
    perCredit: 0,
    perEntity: 0.75,
    usageBased: true,
    type: 'corporate_ownership_add_on',
    moduleName: 'Corporate Ownership Check Add-on',
    priceId: process.env.REACT_APP_CORPORATE_OWNERSHIP_ADDON_PRICE || '',
  },
] as const;
type DatabaseAddOnId = (typeof databaseAddOns)[number]['id'];

const checkoutLicensePriority: Array<'business' | 'pro' | 'standard'> = [
  'business',
  'pro',
  'standard',
];

// How long the confirmation waits for the invoice.paid webhook to assign the
// buyer's seat. It has normally landed already; this only covers a slow one.
const PURCHASED_LICENSE_POLL_ATTEMPTS = 8;
const PURCHASED_LICENSE_POLL_INTERVAL_MS = 1000;

interface ActivePlanDetails {
  id: string;
  subId: string;
  subItemId: string;
  priceId: string;
  quantity: number;
  monthlyTotal: number;
  cancelled: boolean;
  renewsOn: string;
  status: string;
}

interface ActiveCreditPackDetails {
  subId: string;
  subItemId: string;
  priceId: string;
  quantity: number;
  credits: number;
  monthlyTotal: number;
  category: 'database' | 'product_database' | 'analyzers' | 'workflows';
}

interface SubscriptionPricingProps {
  onManageSubscriptions?: () => void;
  handleTabChange: (tab: string) => void;
}

type CreditPackOption =
  | (typeof databaseCreditPacks)[number]
  | (typeof productDatabaseCreditPacks)[number]
  | (typeof analyzersCreditPacks)[number]
  | (typeof workflowsCreditPacks)[number];
type CreditPackSelectionState = {
  selectedCredits: string;
};

const SubscriptionPricing: React.FC<SubscriptionPricingProps> = ({
  handleTabChange,
}: SubscriptionPricingProps) => {
  const { cur_user, refreshSelectedTeamLicenseState } =
    useContext(GlobalContext);
  const location = useLocation();
  const history = useHistory();
  const canManageBilling = cur_user?.role === 'owner';
  // Kicks off the live Stripe credit-pack catalog fetch (Analyzers/
  // Database/Workflows price IDs) as early as possible and re-renders once
  // it resolves -- see CreditCalculator.tsx's useCreditPackCatalog.
  useCreditPackCatalog();
  const [quantities, setQuantities] = useState<Record<string, number>>({
    standard: 0,
    pro: 0,
    business: 0,
  });
  const [creditSelections, setCreditSelections] = useState<CreditSelections>(
    initialCreditSelections,
  );
  // Single selection for the combined "Database Records" section — Entity DB
  // and Product DB are merged into one pack going forward (see the migration
  // plan), so a brand-new purchase only ever creates one subscription now.
  const [databaseCreditSelection, setDatabaseCreditSelection] =
    useState<CreditPackSelectionState>({
      selectedCredits: '',
    });
  const [analyzersCreditSelection, setAnalyzersCreditSelection] =
    useState<CreditPackSelectionState>({
      selectedCredits: '',
    });
  const [workflowsCreditSelection, setWorkflowsCreditSelection] =
    useState<CreditPackSelectionState>({
      selectedCredits: '',
    });
  const [databaseAddOnSelection, setDatabaseAddOnSelection] = useState<
    Record<DatabaseAddOnId, boolean>
  >({ businessVerification: false, corporateOwnership: false });
  const [activeDatabaseAddOns, setActiveDatabaseAddOns] = useState<
    Record<DatabaseAddOnId, boolean>
  >({ businessVerification: false, corporateOwnership: false });
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [isConfirmingLicenseChanges, setIsConfirmingLicenseChanges] =
    useState(false);
  const [activeSubscriptions, setActiveSubscriptions] = useState<
    ActivePlanDetails[]
  >([]);
  const [activeDatabaseCreditPack, setActiveDatabaseCreditPack] =
    useState<ActiveCreditPackDetails | null>(null);
  const [activeProductDatabaseCreditPack, setActiveProductDatabaseCreditPack] =
    useState<ActiveCreditPackDetails | null>(null);
  const [activeAnalyzersCreditPack, setActiveAnalyzersCreditPack] =
    useState<ActiveCreditPackDetails | null>(null);
  const [activeWorkflowsCreditPack, setActiveWorkflowsCreditPack] =
    useState<ActiveCreditPackDetails | null>(null);
  const [creditUsageRefreshToken, setCreditUsageRefreshToken] = useState(0);
  const orderSummaryRef = useRef<HTMLDivElement>(null);
  const [focusPlanId, setFocusPlanId] = useState<string>('business');

  // Real, live Analyzers balance -- same /credits/my-usage endpoint
  // CreditsUsageOverview's "Credits" box reads, fetched here too so the
  // "Analyzers Credits" card in this modal can show a current balance that
  // actually matches it. Analyzers is a one-time (payment_intent) purchase,
  // not a Stripe subscription, so the Stripe-subscription-based
  // activeXCreditPack tracking above can never see it -- this is a separate,
  // necessary fetch.
  const [analyzersBalance, setAnalyzersBalance] = useState<{
    used: number;
    total: number;
  } | null>(null);

  useEffect(() => {
    const teamId = cur_user?.selected_team?.id;
    if (!teamId) {
      setAnalyzersBalance(null);
      return;
    }

    let active = true;
    CreditsAPI.getUsageBreakdown(teamId).then((resp: any) => {
      if (!active || !resp?.success) {
        return;
      }
      const paid = resp.result?.paid ?? {};
      setAnalyzersBalance({
        used: Number(paid.total_used_credits ?? 0),
        total: Number(paid.total_monthly_credits ?? 0),
      });
    });

    // eslint-disable-next-line consistent-return -- standard effect-cleanup
    // pattern: the early exit above intentionally returns nothing, this path
    // returns the cleanup function.
    return () => {
      active = false;
    };
  }, [cur_user?.selected_team?.id, creditUsageRefreshToken]);

  const resetSelections = () => {
    setQuantities({
      standard: 0,
      pro: 0,
      business: 0,
    });
    setDatabaseCreditSelection({ selectedCredits: '' });
    setDatabaseAddOnSelection({
      businessVerification: false,
      corporateOwnership: false,
    });
    setAnalyzersCreditSelection({ selectedCredits: '' });
    setWorkflowsCreditSelection({ selectedCredits: '' });
  };

  const formatUnixDate = (timestamp: number) => {
    if (!timestamp) return 'Current period';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const stripeCustomer = cur_user?.stripe_cus_info?.find(
    (cus: any) => cus?.name === cur_user?.selected_team?.name,
  );
  const selectedTeamAdminEmail =
    cur_user?.selected_team?.attributes?.team_admin?.[0] || '';

  const getActivePlan = (planId: string) =>
    activeSubscriptions.find((subscription) => subscription.id === planId);
  const activeBusinessPlan = getActivePlan('business');
  // const hasActiveBusinessPlan = Boolean(activeBusinessPlan);
  const hasPaidActiveBusinessPlan =
    activeBusinessPlan && activeBusinessPlan.status !== 'trialing';

  const loadActiveSubscriptions = useCallback(async () => {
    const billingLookupEmail =
      selectedTeamAdminEmail || cur_user?.basics?.email || '';
    const selectedTeamName = cur_user?.selected_team?.name;

    let latestStripeCustomer = stripeCustomer;
    if (billingLookupEmail && selectedTeamName) {
      const customerResp = await PaymentsAPI.getCustomerByEmail(
        billingLookupEmail,
      );
      if (customerResp?.success) {
        const allCustomers = Array.isArray(customerResp.result?.data)
          ? customerResp.result.data
          : [];
        latestStripeCustomer =
          allCustomers.find((cus: any) => cus?.name === selectedTeamName) ||
          latestStripeCustomer;
      }
    }

    const subscriptionList = Array.isArray(
      latestStripeCustomer?.subscriptions?.data,
    )
      ? latestStripeCustomer.subscriptions.data
      : [];

    const activeList = subscriptionList.filter((subscription: any) =>
      ['active', 'trialing', 'past_due'].includes(subscription?.status),
    );
    const databasePriceIds = Object.values(databaseCreditPriceIds);
    const productDatabasePriceIds = Object.values(
      productDatabaseCreditPriceIds,
    );
    const analyzersPriceIds = Object.values(analyzersCreditPriceIds);
    const workflowsPriceIds = Object.values(workflowsCreditPriceIds);
    const creditPriceIds = [
      ...databasePriceIds,
      ...productDatabasePriceIds,
      ...analyzersPriceIds,
      ...workflowsPriceIds,
    ].filter(Boolean);

    // Ordered most-specific-first: "product database records pack" must be checked
    // before "database records pack" since it's a substring match, same as before.
    const creditPackCategoryMatchers: Array<{
      category: ActiveCreditPackDetails['category'];
      moduleNameIncludes: string;
      priceIds: string[];
      priceMap: Record<number, string>;
      packs: { credits: number; price: number }[];
    }> = [
      {
        category: 'product_database',
        moduleNameIncludes: 'product database records pack',
        priceIds: productDatabasePriceIds,
        priceMap: productDatabaseCreditPriceIds,
        packs: productDatabaseCreditPacks,
      },
      {
        category: 'database',
        moduleNameIncludes: 'database records pack',
        priceIds: databasePriceIds,
        priceMap: databaseCreditPriceIds,
        packs: databaseCreditPacks,
      },
      {
        category: 'analyzers',
        moduleNameIncludes: 'analyzers credit pack',
        priceIds: analyzersPriceIds,
        priceMap: analyzersCreditPriceIds,
        packs: analyzersCreditPacks,
      },
      {
        category: 'workflows',
        moduleNameIncludes: 'workflows credit pack',
        priceIds: workflowsPriceIds,
        priceMap: workflowsCreditPriceIds,
        packs: workflowsCreditPacks,
      },
    ];

    const details = await Promise.all(
      activeList.map(async (subscription: any) => {
        const rawSubId = subscription?.id;
        const subResp = rawSubId
          ? await PaymentsAPI.getSubscriptionById(rawSubId)
          : null;
        const subData = subResp?.success ? subResp.result : subscription;
        const item = Array.isArray(subData?.items?.data)
          ? subData.items.data[0]
          : null;
        const subItemId = item?.id || '';
        const priceId = item?.price?.id || '';
        const quantity = Number(item?.quantity ?? subscription?.quantity ?? 0);
        const amount = Number(item?.price?.unit_amount ?? 0) / 100;
        const cancelled = Boolean(
          subData?.cancel_at_period_end ?? subscription?.cancel_at_period_end,
        );
        const periodEnd = Number(
          subData?.current_period_end ?? subscription?.current_period_end ?? 0,
        );
        const renewsOn = formatUnixDate(periodEnd);
        const moduleName = (
          subData?.metadata?.moduleName ||
          subscription?.metadata?.moduleName ||
          ''
        ).toLowerCase();
        const matchedAddOn = databaseAddOns.find(
          (a) =>
            moduleName === a.moduleName.toLowerCase() ||
            (a.priceId !== '' && a.priceId === priceId),
        );
        if (matchedAddOn) {
          return { kind: 'addon', addOnId: matchedAddOn.id };
        }
        const isCreditPack =
          moduleName.includes('credit pack') ||
          creditPriceIds.includes(priceId);
        const matchedCategory = creditPackCategoryMatchers.find(
          (m) =>
            moduleName.includes(m.moduleNameIncludes) ||
            m.priceIds.includes(priceId),
        );
        // Legacy Tools/Audit-Tools packs are no longer tracked or sold, so a
        // credit-pack line item that matches none of the categories is ignored.
        if (isCreditPack && !matchedCategory) return null;

        if (isCreditPack && matchedCategory) {
          const category = matchedCategory.category;
          const packPriceMap = matchedCategory.priceMap;
          const availablePacks = matchedCategory.packs;
          const matchedByPrice = availablePacks.find((pack) => {
            const packPriceId =
              packPriceMap[pack.credits as keyof typeof packPriceMap];
            return packPriceId === priceId;
          });
          const moduleCreditsMatch = moduleName.match(/(\d[\d,]*)/);
          const creditsFromName = moduleCreditsMatch
            ? Number(moduleCreditsMatch[1].replace(/,/g, ''))
            : 0;
          const credits = matchedByPrice?.credits || creditsFromName;
          if (!subData?.id || !subItemId || !priceId || !credits) return null;
          return {
            kind: 'credit',
            subId: subData.id,
            subItemId: subItemId,
            priceId: priceId,
            quantity: quantity,
            credits: credits,
            monthlyTotal: amount * quantity,
            category: category,
          };
        }

        const id = resolveLicenseTypeForItem(item, subData) || '';
        if (!id || !subData?.id || !priceId || !subItemId) return null;
        return {
          kind: 'plan',
          id: id,
          subId: subData.id,
          subItemId: subItemId,
          priceId: priceId,
          quantity: quantity,
          monthlyTotal: amount * quantity,
          cancelled: cancelled,
          renewsOn: renewsOn,
          status: subData?.status || subscription?.status || '',
        };
      }),
    );

    const planDetails = details.filter(
      (detail: any) => detail?.kind === 'plan',
    );
    const databaseCreditPackDetail = details.find(
      (detail: any) =>
        detail?.kind === 'credit' && detail?.category === 'database',
    );
    const productDatabaseCreditPackDetail = details.find(
      (detail: any) =>
        detail?.kind === 'credit' && detail?.category === 'product_database',
    );
    const analyzersCreditPackDetail = details.find(
      (detail: any) =>
        detail?.kind === 'credit' && detail?.category === 'analyzers',
    );
    const workflowsCreditPackDetail = details.find(
      (detail: any) =>
        detail?.kind === 'credit' && detail?.category === 'workflows',
    );

    setActiveDatabaseAddOns({
      businessVerification: details.some(
        (d: any) => d?.kind === 'addon' && d.addOnId === 'businessVerification',
      ),
      corporateOwnership: details.some(
        (d: any) => d?.kind === 'addon' && d.addOnId === 'corporateOwnership',
      ),
    });
    setActiveSubscriptions(planDetails as ActivePlanDetails[]);
    setActiveDatabaseCreditPack(
      (databaseCreditPackDetail as ActiveCreditPackDetails) || null,
    );
    setActiveProductDatabaseCreditPack(
      (productDatabaseCreditPackDetail as ActiveCreditPackDetails) || null,
    );
    setActiveAnalyzersCreditPack(
      (analyzersCreditPackDetail as ActiveCreditPackDetails) || null,
    );
    setActiveWorkflowsCreditPack(
      (workflowsCreditPackDetail as ActiveCreditPackDetails) || null,
    );
  }, [
    cur_user?.basics?.email,
    cur_user?.selected_team?.name,
    selectedTeamAdminEmail,
    stripeCustomer,
  ]);

  useEffect(() => {
    loadActiveSubscriptions();
  }, [loadActiveSubscriptions]);

  // Changing tier on an already-active recurring credit pack (Database,
  // Workflows), or merging Database's two legacy subscriptions into one, no
  // longer happens on selection -- picking a new tier just stages it (like
  // any other pack) so it shows up in the Plan Summary and goes through the
  // normal Checkout review/confirm flow. CheckoutModal is what actually
  // calls PaymentsAPI.changePlan / mergeDatabaseCreditSubscriptions once the
  // user confirms there.

  // useEffect(() => {
  //   const activeBusinessPlan = getActivePlan('business');
  //   const isBusinessTrial = activeBusinessPlan?.status === 'trialing';

  //   if (!isBusinessTrial) {
  //     return;
  //   }

  //   setQuantities((prev) => {
  //     if ((prev.business || 0) > 0) {
  //       return prev;
  //     }

  //     return {
  //       ...prev,
  //       business: Math.max(1, Number(activeBusinessPlan?.quantity || 1)),
  //     };
  //   });
  // }, [activeSubscriptions]);

  const refreshBillingUI = useCallback(async () => {
    await loadActiveSubscriptions();
    setCreditUsageRefreshToken((prev) => prev + 1);
  }, [loadActiveSubscriptions]);

  // The invoice.paid webhook assigns the buyer's seat a second or two after
  // payment. Wait briefly for it before refreshing, so the license state and
  // the Assign tab don't show the buyer unlicensed in the meantime. The
  // webhook is the only thing that assigns it, so if it hasn't arrived, say
  // so rather than leave the buyer unlicensed without knowing why.
  const waitForPurchasedLicense = async (license: string) => {
    const teamId = cur_user?.selected_team?.id;
    const userId = cur_user?.basics?.id;
    if (!teamId || !userId) return;

    let assigned = false;
    for (
      let attempt = 0;
      attempt < PURCHASED_LICENSE_POLL_ATTEMPTS && !assigned;
      attempt += 1
    ) {
      // eslint-disable-next-line no-await-in-loop -- each poll follows the last
      const resp = await LicensesAPI.getMyLicense(teamId, String(userId));
      assigned = Boolean(resp?.success && resp.result?.license === license);
      if (!assigned) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) =>
          setTimeout(resolve, PURCHASED_LICENSE_POLL_INTERVAL_MS),
        );
      }
    }
    await refreshSelectedTeamLicenseState();
    if (!assigned) {
      enqueueSnackbar(
        'Your new license is taking longer than usual to apply. If it is not active in a minute, assign it to yourself from the Assign tab.',
        { variant: 'warning' },
      );
    }
  };

  const cancelTrialSubscriptions = useCallback(async () => {
    const trialSubscriptions = activeSubscriptions.filter(
      (subscription) =>
        subscription.status === 'trialing' && subscription.subId,
    );

    if (!trialSubscriptions.length) {
      return;
    }

    await Promise.all(
      trialSubscriptions.map((subscription) =>
        PaymentsAPI.cancelSubscription(subscription.subId),
      ),
    );
  }, [activeSubscriptions]);

  const updateQuantity = (planId: string, delta: number) =>
    canManageBilling &&
    setQuantities((prev) => ({
      ...prev,
      [planId]: Math.max(0, (prev[planId] || 0) + delta),
    }));

  const setQuantityFromInput = (planId: string, value: string) => {
    if (!canManageBilling) return;
    const parsed = Number.parseInt(value, 10);
    setQuantities((prev) => ({
      ...prev,
      [planId]: Number.isFinite(parsed) ? Math.max(0, parsed) : 0,
    }));
  };

  const manageInitialSubscriptions = useMemo(
    () =>
      plans
        .map((plan) => {
          const active = getActivePlan(plan.id);
          if (!active) return null;
          return {
            id: plan.id,
            subId: active.subId,
            subItemId: active.subItemId,
            priceId: active.priceId,
            name: plan.name,
            accent: plan.accent,
            pricePerUnit: plan.price,
            quantity: active.quantity,
            renewsOn: active.renewsOn,
            cancelled: active.cancelled,
            status: active.status,
          };
        })
        .filter(Boolean),
    [activeSubscriptions],
  );

  // Powers inline editing of already-active plan quantities directly on the
  // plan cards (when canManageBilling), plus the Plan Summary panel below —
  // shares the exact upgrade/downgrade/proration logic ManageSubscriptions.tsx
  // uses in its "Manage My Subscriptions" modal, so a change made either way
  // is billed identically.
  const licenseManager = useActiveLicenseManager({
    subscriptions: manageInitialSubscriptions as any,
    customerId: stripeCustomer?.id,
    onSubscriptionsUpdated: refreshBillingUI,
  });
  const [licenseConfirmOpen, setLicenseConfirmOpen] = useState(false);

  const hasTrialingSubscription = activeSubscriptions.some(
    (subscription) => subscription.status === 'trialing',
  );

  const totalLicenses = Object.values(quantities).reduce((a, b) => a + b, 0);
  const highestCheckoutLicense =
    checkoutLicensePriority.find((tier) => (quantities[tier] || 0) > 0) || '';
  // A team's first licence purchase (converting a trial counts) gives the
  // buyer a seat of the highest tier bought; later purchases leave seats to
  // the Assign tab.
  const hasAnyPaidSubscription = activeSubscriptions.some(
    (subscription) => subscription.status !== 'trialing',
  );
  const purchaserAutoAssignLicense =
    !hasAnyPaidSubscription && highestCheckoutLicense
      ? highestCheckoutLicense
      : undefined;
  const totalMonthly = plans.reduce(
    (sum, plan) => sum + plan.price * (quantities[plan.id] || 0),
    0,
  );
  const totalCredits = 0;
    //(quantities.pro > 0 ? 25 : 0) + (quantities.business > 0 ? 25 : 0);
  const creditCalc = getCreditCalcTotals(creditSelections);
  const selectedDatabasePack = databaseCreditSelection.selectedCredits
    ? databaseCreditPacks.find(
        (p) => p.credits === Number(databaseCreditSelection.selectedCredits),
      ) ?? null
    : null;
  // ── Combined Database pack ──────────────────────────────────────────────
  // Conversion step: merge the two legacy Stripe subscriptions (Entity DB +
  // Product DB) into one view BEFORE anything renders, so the page only ever
  // shows a single "Database Records" balance/section. The underlying Stripe
  // subscriptions are still two separate objects until the migration in the
  // plan runs — this is a display/UX merge, not a billing merge. While a team
  // has both active, tier editing is locked (see hasBothLegacyDatabaseSubscriptions)
  // rather than guessing how to split an edit across two real subscriptions.
  const hasAnyActiveDatabasePack = Boolean(
    activeDatabaseCreditPack || activeProductDatabaseCreditPack,
  );
  const hasBothLegacyDatabaseSubscriptions = Boolean(
    activeDatabaseCreditPack && activeProductDatabaseCreditPack,
  );
  const combinedActiveDatabaseCredits =
    (activeDatabaseCreditPack?.credits ?? 0) +
    (activeProductDatabaseCreditPack?.credits ?? 0);
  const combinedActiveDatabaseMonthlyTotal =
    (activeDatabaseCreditPack?.monthlyTotal ?? 0) +
    (activeProductDatabaseCreditPack?.monthlyTotal ?? 0);
  const databaseTierOptions = databaseCreditPacks.filter(
    (p) => p.credits !== 200000,
  );
  // The raw combined total rarely lands on a standard tier (e.g. entity 400 +
  // product 800 = 1200, not a listed package) — round UP to the closest real
  // package so it always resolves to one of the normal, purchasable options
  // rather than showing as a separate "custom" entry a team could reselect.
  const roundedCombinedDatabasePack = hasAnyActiveDatabasePack
    ? databaseTierOptions.find(
        (p) => p.credits >= combinedActiveDatabaseCredits,
      ) ?? databaseTierOptions[databaseTierOptions.length - 1]
    : null;
  const activeDatabasePackOption = roundedCombinedDatabasePack;
  const displayedDatabasePack =
    selectedDatabasePack || activeDatabasePackOption;
  const activeDatabasePackCreditsValue = roundedCombinedDatabasePack
    ? String(roundedCombinedDatabasePack.credits)
    : '';
  const databaseSelectValue =
    databaseCreditSelection.selectedCredits || activeDatabasePackCreditsValue;
  const selectedAnalyzersPack = analyzersCreditSelection.selectedCredits
    ? analyzersCreditPacks.find(
        (p) => p.credits === Number(analyzersCreditSelection.selectedCredits),
      ) ?? null
    : null;
  const selectedWorkflowsPack = workflowsCreditSelection.selectedCredits
    ? workflowsCreditPacks.find(
        (p) => p.credits === Number(workflowsCreditSelection.selectedCredits),
      ) ?? null
    : null;
  const getPackMonthlyPrice = (
    pack: ActiveCreditPackDetails | CreditPackOption | null | undefined,
  ) => {
    if (!pack) {
      return 0;
    }
    return 'price' in pack ? pack.price : pack.monthlyTotal;
  };
  const hasSelectedBusinessPlan = quantities.business > 0;
  const hasSelectedProOrBusinessPlan =
    quantities.pro > 0 || quantities.business > 0;
  // A Database pack needs at least one Business license: either one being
  // added in this order, or a paid active Business plan that still has
  // licenses after any pending edit (taking it to 0, or a scheduled
  // cancellation, means it no longer counts).
  const activeBusinessSub = licenseManager.subscriptions.find(
    (s) => s.id === 'business',
  );
  const activeBusinessLicenses =
    hasPaidActiveBusinessPlan && !activeBusinessSub?.cancelled
      ? licenseManager.pendingQuantities.business ??
        Number(activeBusinessPlan?.quantity ?? 0)
      : 0;
  const canPurchaseDatabaseCredits =
    hasSelectedBusinessPlan || activeBusinessLicenses > 0;
  const databaseNeedsBusinessLicense =
    Boolean(selectedDatabasePack) && !canPurchaseDatabaseCredits;
  const blockTrialOneTimeAnalyzersPurchase =
    hasTrialingSubscription &&
    Boolean(selectedAnalyzersPack) &&
    !hasSelectedProOrBusinessPlan;
  const checkoutAnalyzersPack = blockTrialOneTimeAnalyzersPurchase
    ? null
    : selectedAnalyzersPack;
  const blockTrialOneTimeWorkflowsPurchase =
    hasTrialingSubscription &&
    Boolean(selectedWorkflowsPack) &&
    !hasSelectedProOrBusinessPlan;
  const checkoutWorkflowsPack = blockTrialOneTimeWorkflowsPurchase
    ? null
    : selectedWorkflowsPack;
  const selectedAnalyzersOneTimePrice = getPackMonthlyPrice(
    checkoutAnalyzersPack,
  );
  // Workflows is monthly recurring now (was one-time) -- folded into
  // monthlyGrandTotal below, not oneTimeTotal.
  const selectedWorkflowsMonthlyPrice = getPackMonthlyPrice(
    checkoutWorkflowsPack,
  );
  const selectedDatabaseMonthlyPrice =
    getPackMonthlyPrice(selectedDatabasePack);
  // Add-ons are priced off the Database tier in play (newly selected, else the
  // current plan's tier).
  const addOnCreditBasis = displayedDatabasePack?.credits ?? 0;
  const selectedDatabaseAddOns = databaseAddOns.filter(
    (a) => databaseAddOnSelection[a.id] && !activeDatabaseAddOns[a.id],
  );
  const databaseAddOnsMonthlyPrice = selectedDatabaseAddOns.reduce(
    (sum, a) => sum + (a.usageBased ? 0 : a.perCredit * addOnCreditBasis),
    0,
  );
  // The new recurring total must carry every pack that will still be billed,
  // not just the ones picked in this cart: currentlyBilledMonthlyTotal below
  // subtracts the active Database/Workflows packs, so leaving them out here
  // ate the increase from any other change (extra licenses, a new pack) and
  // "Due now" read $0 for teams that already had one.
  const effectiveDatabaseMonthlyPrice = selectedDatabasePack
    ? selectedDatabaseMonthlyPrice
    : combinedActiveDatabaseMonthlyTotal;
  const effectiveWorkflowsMonthlyPrice = checkoutWorkflowsPack
    ? selectedWorkflowsMonthlyPrice
    : activeWorkflowsCreditPack?.monthlyTotal ?? 0;
  const monthlyGrandTotal =
    totalMonthly +
    effectiveDatabaseMonthlyPrice +
    effectiveWorkflowsMonthlyPrice +
    databaseAddOnsMonthlyPrice;
  const oneTimeTotal = selectedAnalyzersOneTimePrice;
  // Recurring amount for only what this cart newly purchases. Unlike
  // monthlyGrandTotal it excludes already-active packs (they're carried there
  // purely for the Plan Summary's new monthly total), so the Checkout modal
  // never bills or lists plans the user isn't touching.
  const checkoutMonthlyTotal =
    totalMonthly +
    (selectedDatabasePack ? selectedDatabaseMonthlyPrice : 0) +
    (checkoutWorkflowsPack ? selectedWorkflowsMonthlyPrice : 0) +
    databaseAddOnsMonthlyPrice;
  // Combined Plan Summary total — folds in pending edits to already-active
  // plans (licenseManager.newTotal) alongside brand-new selections/credits.
  const combinedMonthlyTotal = monthlyGrandTotal + licenseManager.newTotal;
  // Prorated charge for pending license-quantity upgrades — the same figure
  // the license Confirm modal and CheckoutModal's licenseUpgrade bill today.
  const licenseUpgradeDueNow = licenseManager.hasPendingChanges
    ? Math.max(0, licenseManager.quantityUpgradeProrationDueToday ?? 0)
    : 0;
  // Must equal what the modals charge: new purchases in full, license upgrades
  // prorated, plus one-time credits. (It used to be the full monthly delta,
  // which overstated license upgrades.)
  // A tier change on an already-active Database/Workflows pack updates the
  // existing subscription in place (PaymentsAPI.changePlan), so Stripe bills
  // only the prorated difference today -- not the new tier's full monthly
  // price that checkoutMonthlyTotal carries. `monthlyFull` is that full price
  // (to subtract) and `dueToday` the previewed proration. Null until the
  // preview resolves, or if it fails -- then the full price stays in the total.
  const [tierChangeBilling, setTierChangeBilling] = useState<{
    monthlyFull: number;
    dueToday: number;
  } | null>(null);
  const tierChangeAdjustment = tierChangeBilling
    ? tierChangeBilling.dueToday - tierChangeBilling.monthlyFull
    : 0;
  const dueNow =
    checkoutMonthlyTotal +
    tierChangeAdjustment +
    oneTimeTotal +
    licenseUpgradeDueNow;
  const checkoutSubscriptions: CheckoutSubscriptionItem[] = plans
    .filter((plan) => (quantities[plan.id] || 0) > 0)
    .map((plan) => {
      const priceMap: Record<string, string> = {
        standard: standardPrice,
        pro: proPrice,
        business: businessPrice,
      };
      const includedAnalysisCreditsMode: CheckoutSubscriptionItem['includedAnalysisCreditsMode'] =
        'one_time';
      return {
        priceId: priceMap[plan.id],
        quantity: quantities[plan.id],
        moduleName: `BITE ${plan.name}`,
        type: 'fixed' as const,
        includedAnalysisCreditsMode: includedAnalysisCreditsMode,
        autoAssignLicense:
          plan.id === purchaserAutoAssignLicense
            ? purchaserAutoAssignLicense
            : undefined,
      };
    })
    .filter((item) => Boolean(item.priceId));

  const activeSingleDatabaseCreditPack =
    activeDatabaseCreditPack || activeProductDatabaseCreditPack;
  if (selectedDatabasePack && canPurchaseDatabaseCredits) {
    // Selecting a tier no longer applies to Stripe on selection -- it's
    // staged here (new subscription / tier change / legacy-pair merge) and
    // only actually happens once the user confirms in CheckoutModal.
    const selectedDatabasePackPriceId =
      databaseCombinedCreditPriceIds[
        selectedDatabasePack.credits as keyof typeof databaseCombinedCreditPriceIds
      ];
    if (selectedDatabasePackPriceId) {
      if (
        hasBothLegacyDatabaseSubscriptions &&
        activeDatabaseCreditPack &&
        activeProductDatabaseCreditPack
      ) {
        checkoutSubscriptions.push({
          priceId: selectedDatabasePackPriceId,
          quantity: 1,
          moduleName: `Database Credit Pack ${selectedDatabasePack.credits}`,
          type: 'database_credit_merge',
          subId: activeDatabaseCreditPack.subId,
          subItemId: activeDatabaseCreditPack.subItemId,
          secondarySubId: activeProductDatabaseCreditPack.subId,
        });
      } else if (activeSingleDatabaseCreditPack) {
        // Single active pack (either legacy Entity DB or legacy Product DB, or
        // an already-combined one) -- only a real tier change is worth staging.
        if (
          selectedDatabasePack.credits !==
          activeSingleDatabaseCreditPack.credits
        ) {
          checkoutSubscriptions.push({
            priceId: selectedDatabasePackPriceId,
            quantity: 1,
            moduleName: `Database Credit Pack ${selectedDatabasePack.credits}`,
            type: 'database_credit_change',
            subId: activeSingleDatabaseCreditPack.subId,
            subItemId: activeSingleDatabaseCreditPack.subItemId,
          });
        }
      } else {
        // Brand-new purchase always goes through the new combined Database
        // Credits price ladder, never the legacy databaseCreditPriceIds map
        // (which stays reserved for classifying existing pre-merge subscribers).
        checkoutSubscriptions.push({
          priceId: selectedDatabasePackPriceId,
          quantity: 1,
          moduleName: `Database Credit Pack ${selectedDatabasePack.credits}`,
          type: 'database_credit',
        });
      }
    }
  }
  selectedDatabaseAddOns.forEach((addOn) => {
    if (!addOn.priceId || addOnCreditBasis <= 0) {
      return;
    }
    checkoutSubscriptions.push({
      priceId: addOn.priceId,
      // Metered prices take no quantity (CheckoutModal omits it for them).
      quantity: addOn.usageBased ? 1 : addOnCreditBasis,
      moduleName: addOn.moduleName,
      type: addOn.type,
    });
  });
  if (checkoutAnalyzersPack) {
    const selectedAnalyzersPackPriceId =
      analyzersCreditPriceIds[
        checkoutAnalyzersPack.credits as keyof typeof analyzersCreditPriceIds
      ];
    if (selectedAnalyzersPackPriceId) {
      checkoutSubscriptions.push({
        priceId: selectedAnalyzersPackPriceId,
        quantity: 1,
        moduleName: `Analyzers Credit Pack ${checkoutAnalyzersPack.credits}`,
        type: 'analyzers_credit',
      });
    }
  }
  if (checkoutWorkflowsPack) {
    const selectedWorkflowsPackPriceId =
      workflowsCreditPriceIds[
        checkoutWorkflowsPack.credits as keyof typeof workflowsCreditPriceIds
      ];
    if (selectedWorkflowsPackPriceId) {
      if (activeWorkflowsCreditPack) {
        if (
          checkoutWorkflowsPack.credits !== activeWorkflowsCreditPack.credits
        ) {
          checkoutSubscriptions.push({
            priceId: selectedWorkflowsPackPriceId,
            quantity: 1,
            moduleName: `Workflows Credit Pack ${checkoutWorkflowsPack.credits}`,
            type: 'workflows_credit_change',
            subId: activeWorkflowsCreditPack.subId,
            subItemId: activeWorkflowsCreditPack.subItemId,
          });
        }
      } else {
        checkoutSubscriptions.push({
          priceId: selectedWorkflowsPackPriceId,
          quantity: 1,
          moduleName: `Workflows Credit Pack ${checkoutWorkflowsPack.credits}`,
          type: 'workflows_credit',
        });
      }
    }
  }

  const tierChangeSignature = checkoutSubscriptions
    .filter(
      (item) =>
        (item.type === 'database_credit_change' ||
          item.type === 'workflows_credit_change') &&
        item.subId,
    )
    .map((item) => `${item.type}|${item.subId}|${item.priceId}`)
    .join(',');
  useEffect(() => {
    const customerId = stripeCustomer?.id;
    const changes = checkoutSubscriptions.filter(
      (item) =>
        (item.type === 'database_credit_change' ||
          item.type === 'workflows_credit_change') &&
        item.subId,
    );
    if (!customerId || changes.length === 0) {
      setTierChangeBilling(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        let dueToday = 0;
        let monthlyFull = 0;
        for (const change of changes) {
          const previewResp = await PaymentsAPI.previewSubPayment(
            change.subId as string,
            customerId,
            change.priceId,
            1,
          );
          const amount = previewResp?.success
            ? getNetProrationAmount(previewResp.result)
            : null;
          if (amount === null) {
            // Can't price this change -- leave the full price in the total.
            if (!cancelled) setTierChangeBilling(null);
            return;
          }
          // A downgrade is not invoiced (pending credit only): bills $0 today.
          dueToday += Math.max(0, amount);
          monthlyFull +=
            change.type === 'database_credit_change'
              ? selectedDatabaseMonthlyPrice
              : selectedWorkflowsMonthlyPrice;
        }
        if (!cancelled) {
          setTierChangeBilling({ monthlyFull: monthlyFull, dueToday: dueToday });
        }
      } catch {
        if (!cancelled) setTierChangeBilling(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tierChangeSignature, stripeCustomer?.id]);

  // Deliberately independent of checkoutSubscriptions: that list only
  // includes a pack once its Stripe price id env var actually resolves
  // (REACT_APP_TARIFF_CREDITS_*/REACT_APP_WORKFLOW_CREDITS_* don't exist
  // yet — see CreditCalculator.tsx). Without this, selecting Analyzers/
  // Workflows credits before that catalog work lands would silently produce
  // zero checkout line items, hasSelection would stay false, and the Plan
  // Summary panel would never appear no matter how long anything waits for
  // it. The panel should reflect what the user picked in the UI; whether
  // it's actually purchasable yet is a separate (real) checkout-time concern.
  const hasSelection =
    Boolean(selectedDatabasePack) ||
    databaseAddOnsMonthlyPrice > 0 ||
    selectedDatabaseAddOns.length > 0 ||
    Boolean(checkoutAnalyzersPack) ||
    Boolean(checkoutWorkflowsPack) ||
    totalLicenses > 0 ||
    creditCalc.totalCredits > 0;

  // hasSelection deliberately stays true for a picked pack whose Stripe price
  // hasn't resolved (see above), so Checkout itself has to refuse it --
  // otherwise it opens with that pack silently missing from the order.
  const hasUnconfiguredCreditPack =
    Boolean(
      selectedDatabasePack &&
        !databaseCombinedCreditPriceIds[
          selectedDatabasePack.credits as keyof typeof databaseCombinedCreditPriceIds
        ],
    ) ||
    Boolean(
      checkoutAnalyzersPack &&
        !analyzersCreditPriceIds[
          checkoutAnalyzersPack.credits as keyof typeof analyzersCreditPriceIds
        ],
    ) ||
    Boolean(
      checkoutWorkflowsPack &&
        !workflowsCreditPriceIds[
          checkoutWorkflowsPack.credits as keyof typeof workflowsCreditPriceIds
        ],
    );

  useEffect(() => {
    // Only fire if the URL hash matches one of our section IDs
    const id = location.hash.slice(1);
    if (id === 'credits-section' || id === 'put-bite-to-work') {
      // A slight delay ensures React has finished painting the DOM
      // and layout shifts from your 'isLoading' state are resolved.
      const timer = setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({
          behavior: 'smooth',
          block: id === 'put-bite-to-work' ? 'start' : 'center',
        });
        // One-shot: drop the hash so later visits to this tab don't re-scroll
        if (id === 'put-bite-to-work') {
          history.replace({ ...location, hash: '' });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  return (
    <div className='msp-root' data-readonly={!canManageBilling || undefined}>
      {!canManageBilling && (
        <div
          className='msp-info-row'
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <IconZap />
          <span className='msp-info-label' style={{ fontSize: '15px' }}>
            Contact your team owner to manage your subscriptions
          </span>
        </div>
      )}
      <div className='msp-hero'>
        <h2 className='msp-hero-title'>
          Simple pricing for however you put <strong>BITE</strong> to work.
        </h2>
        <p className='msp-hero-sub'>
          Choose the tools your team needs. Run compliance workflows when you
          need them. Continuously monitor the products and entities that matter.
        </p>
        <div className='msp-hero-chips'>
          <span className='msp-hero-chip'>
            <IconShieldCheck /> Access
          </span>
          <IconArrowRight />
          <span className='msp-hero-chip'>
            <IconZap /> Work
          </span>
          <IconArrowRight />
          <span className='msp-hero-chip'>
            <IconTarget /> Monitor
          </span>
        </div>
      </div>

      {/* Plan descriptions */}
      <div className='msp-plan-details'>
        <div className='plan-grid'>
          {plans.map((plan) => (
            <div
              key={plan.id}
              className='plan-card'
              data-featured={plan.id === 'pro' || undefined}
            >
              {plan.badge && (
                <div className='msp-badge-wrap'>
                  <span
                    className='plan-badge'
                    style={{ backgroundColor: plan.accent }}
                  >
                    {plan.badge}
                  </span>
                </div>
              )}
              <div className='msp-plan-header'>
                <h3 className='msp-plan-name' style={{ color: plan.accent }}>
                  {plan.name}
                </h3>
                <p className='msp-plan-desc'>{plan.description}</p>
              </div>
              <div className='msp-plan-price'>
                <span className='msp-price-amount'>${plan.price}</span>
                <span className='msp-price-period'>/ user / mo</span>
              </div>
              <div className='msp-features'>
                <p className='msp-features-section-label'>Capabilities</p>
                <ul className='msp-feature-list'>
                  {plan.capabilities.map((feature, i) => (
                    <li key={i} className='msp-feature-item'>
                      <span className='feature-check'>
                        <IconCheck />
                      </span>
                      <span className='msp-feature-text'>{feature}</span>
                    </li>
                  ))}
                </ul>
                <p className='msp-features-section-label msp-features-section-label--outcomes'>
                  Outcomes
                </p>
                <ul className='msp-feature-list'>
                  {plan.outcomes.map((feature, i) => (
                    <li key={i} className='msp-feature-item'>
                      <span className='feature-check'>
                        <IconTarget />
                      </span>
                      <span className='msp-feature-text'>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Builder: numbered steps + sticky estimate (matches the approved pricing design) */}
      <div className='msp-builder'>
        <div className='msp-builder-main'>
          <div className='msp-step'>
            <div className='msp-step-head'>
              <span className='msp-step-num'>1</span>
              <div>
                <h3 className='msp-step-title'>Choose your plan</h3>
                <p className='msp-step-sub'>
                  Access — a monthly per-user subscription.
                </p>
              </div>
            </div>
            <div className='msp-step-body'>
              {(() => {
                const planViews = plans.map((plan) => {
                  const qty = quantities[plan.id] || 0;
                  const activePlan = getActivePlan(plan.id);
                  const isActivePlan = Boolean(activePlan);
                  const isTrialingPlan = activePlan?.status === 'trialing';
                  const isBusinessTrial =
                    plan.id === 'business' && isTrialingPlan;
                  const licenseSub = licenseManager.subscriptions.find(
                    (s) => s.id === plan.id,
                  );
                  const isCancelled =
                    licenseSub?.cancelled ?? activePlan?.cancelled ?? false;
                  // Already-active (paid, non-trial) plans are editable inline once the
                  // caller can manage billing — the resulting quantity change routes
                  // through licenseManager (upgrade now / downgrade scheduled), the
                  // same logic ManageSubscriptions.tsx's modal uses.
                  const isEditableActivePlan =
                    isActivePlan && !isBusinessTrial && canManageBilling;
                  const activeScheduledDowngrade =
                    licenseManager.scheduledDowngrades[plan.id];
                  const isActivePlanChangeLocked = Boolean(
                    activeScheduledDowngrade,
                  );
                  const activePendingQty =
                    licenseManager.pendingQuantities[plan.id] ??
                    Number(activePlan?.quantity || 0);
                  const selectedQty = isEditableActivePlan
                    ? activePendingQty
                    : qty;
                  const displayedQty =
                    isActivePlan && !isBusinessTrial
                      ? isEditableActivePlan
                        ? activePendingQty
                        : Number(activePlan?.quantity || 0)
                      : selectedQty;
                  const disablePlanEdits =
                    isActivePlan && !isBusinessTrial && !canManageBilling;
                  const quantityInputValue =
                    disablePlanEdits || displayedQty > 0 || isEditableActivePlan
                      ? String(displayedQty)
                      : '';
                  const disablePlanActions =
                    disablePlanEdits ||
                    !canManageBilling ||
                    (isEditableActivePlan &&
                      (isActivePlanChangeLocked || isCancelled));
                  const decrementDisabled = isEditableActivePlan
                    ? selectedQty <= 0 || disablePlanActions
                    : selectedQty === 0 || disablePlanActions;
                  const handleDecrement = () =>
                    isEditableActivePlan
                      ? licenseManager.updateQty(plan.id, -1)
                      : updateQuantity(plan.id, -1);
                  const handleIncrement = () =>
                    isEditableActivePlan
                      ? licenseManager.updateQty(plan.id, 1)
                      : updateQuantity(plan.id, 1);
                  const handleQtyInputChange = (value: string) => {
                    if (isEditableActivePlan) {
                      const parsed = Number.parseInt(value, 10);
                      licenseManager.setQtyFromInput(
                        plan.id,
                        Math.max(0, Number.isFinite(parsed) ? parsed : 0),
                      );
                      return;
                    }
                    setQuantityFromInput(plan.id, value);
                  };
                  const isFocused = focusPlanId === plan.id;
                  return {
                    id: plan.id,
                    tile: (
                      <button
                        key={plan.id}
                        type='button'
                        role='radio'
                        aria-checked={isFocused}
                        className='msp-plan-tile'
                        data-focused={isFocused || undefined}
                        onClick={() => setFocusPlanId(plan.id)}
                      >
                        <span className='msp-plan-tile-name'>
                          {plan.name}
                          {isActivePlan ? (
                            <span className='msp-plan-tile-tag'>
                              {isBusinessTrial ? 'Free Trial' : 'Active'}
                            </span>
                          ) : plan.id === 'business' ? (
                            <span className='msp-plan-tile-tag'>
                              Recommended
                            </span>
                          ) : null}
                        </span>
                        <span className='msp-plan-tile-price'>
                          ${plan.price}
                          <span> /user/mo</span>
                        </span>
                        {displayedQty > 0 && (
                          <span className='msp-plan-tile-qty'>
                            {displayedQty}{' '}
                            {displayedQty === 1 ? 'license' : 'licenses'}
                          </span>
                        )}
                      </button>
                    ),
                    controls: (
                      <div
                        key={plan.id}
                        className='msp-plan-controls'
                        data-active={isActivePlan || undefined}
                      >
                        <div className='msp-plan-header'>
                          <div className='msp-plan-title-row'>
                            <h3
                              className='msp-plan-name'
                              style={{ color: plan.accent }}
                            >
                              {plan.name}
                            </h3>
                            {isActivePlan &&
                              !isBusinessTrial &&
                              !isCancelled && (
                                <span className='msp-inline-active-badge'>
                                  Active
                                </span>
                              )}
                            {isActivePlan &&
                              !isBusinessTrial &&
                              isCancelled && (
                                <span className='pricing-badge-cancelled'>
                                  Cancels {activePlan?.renewsOn}
                                </span>
                              )}
                            {isBusinessTrial && (
                              <span className='msp-inline-active-badge'>
                                Free Trial
                              </span>
                            )}
                            {isEditableActivePlan && licenseSub && (
                              <button
                                type='button'
                                className={
                                  isCancelled
                                    ? 'pricing-btn pricing-btn-outline pricing-btn-sm'
                                    : 'ms-cancel-btn'
                                }
                                style={{ marginLeft: 'auto' }}
                                onClick={() =>
                                  licenseManager.requestToggleCancel(licenseSub)
                                }
                              >
                                {isCancelled ? (
                                  <>
                                    <IconRotateCcw /> Renew
                                  </>
                                ) : (
                                  <>
                                    <IconX /> Cancel
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                          {isCancelled && (
                            <div className='ms-cancel-indicator'>
                              <IconAlertTriangle />
                              <span>
                                Scheduled to cancel on {activePlan?.renewsOn}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className='msp-qty-section'>
                          {isEditableActivePlan && isActivePlanChangeLocked && (
                            <div className='ms-cancel-indicator'>
                              <IconAlertTriangle />
                              <span>
                                Downgrade to{' '}
                                {activeScheduledDowngrade.targetQuantity}{' '}
                                licenses is scheduled for the next billing
                                period.
                              </span>
                              <button
                                type='button'
                                className='pricing-btn pricing-btn-outline pricing-btn-sm'
                                onClick={() =>
                                  licenseManager.cancelScheduledDowngrade(
                                    plan.id,
                                  )
                                }
                              >
                                <IconRotateCcw /> Cancel scheduled change
                              </button>
                            </div>
                          )}
                          <label className='msp-qty-label'>
                            {isActivePlan && !isBusinessTrial
                              ? isEditableActivePlan
                                ? 'Licenses'
                                : 'Current active licenses'
                              : 'How many users?'}
                          </label>
                          <div className='qty-selector'>
                            <button
                              className='pricing-btn pricing-btn-outline pricing-btn-icon-sm'
                              onClick={handleDecrement}
                              disabled={decrementDisabled}
                            >
                              <IconMinus />
                            </button>
                            <div className='qty-display'>
                              <input
                                type='number'
                                min={isEditableActivePlan ? 1 : 0}
                                step={1}
                                className='number'
                                value={quantityInputValue}
                                placeholder='0'
                                onChange={(e) =>
                                  handleQtyInputChange(e.target.value)
                                }
                                disabled={disablePlanActions}
                              />
                              <span className='suffix'>
                                {displayedQty === 1 ? 'license' : 'licenses'}
                              </span>
                            </div>
                            <button
                              className='pricing-btn pricing-btn-outline pricing-btn-icon-sm'
                              onClick={handleIncrement}
                              disabled={disablePlanActions}
                            >
                              <IconPlus />
                            </button>
                          </div>
                        </div>
                        <p className='msp-subtotal'>
                          Subtotal:{' '}
                          <span className='msp-subtotal-value'>
                            $
                            {isBusinessTrial
                              ? plan.price * selectedQty
                              : isActivePlan
                              ? plan.price * displayedQty
                              : plan.price * qty}
                            /mo
                          </span>
                        </p>
                        <button
                          className='msp-plan-cta'
                          onClick={() => {
                            if (!canManageBilling) {
                              return;
                            }
                            if (!isActivePlan && qty === 0) {
                              updateQuantity(plan.id, 1);
                              return;
                            }

                            if (isBusinessTrial && qty === 0) {
                              setQuantities((prev) => ({
                                ...prev,
                                business: Math.max(
                                  1,
                                  Number(activePlan?.quantity || 1),
                                ),
                              }));
                            }
                          }}
                          disabled={
                            disablePlanEdits ||
                            (!canManageBilling && !isActivePlan)
                          }
                          data-active={isActivePlan || undefined}
                        >
                          {isBusinessTrial && selectedQty > 0 ? (
                            <>
                              <IconShoppingCart /> Purchase {selectedQty}{' '}
                              {selectedQty === 1 ? 'license' : 'licenses'}
                            </>
                          ) : isBusinessTrial && selectedQty === 0 ? (
                            <>
                              Upgrade to Paid <IconArrowRight />
                            </>
                          ) : isActivePlan && !isBusinessTrial ? (
                            <>
                              <IconCheck /> Active ({displayedQty}{' '}
                              {displayedQty === 1 ? 'license' : 'licenses'})
                              {displayedQty !== activePlan?.quantity &&
                                ' — pending'}
                            </>
                          ) : selectedQty > 0 ? (
                            <>
                              <IconCheck /> {selectedQty}{' '}
                              {selectedQty === 1 ? 'license' : 'licenses'} added
                            </>
                          ) : (
                            <>
                              {plan.cta} <IconArrowRight />
                            </>
                          )}
                        </button>
                        {isBusinessTrial && (
                          <p
                            className='msp-plan-trial-note'
                            style={{
                              textAlign: 'center',
                              marginTop: '0.5rem',
                              fontSize: '13px',
                              color: 'var(--text-muted, #6b7280)',
                            }}
                          >
                            This tier is currently active as your free trial.
                          </p>
                        )}
                        {isActivePlan && !isBusinessTrial && (
                          <p className='msp-active-total'>
                            Current monthly total:{' '}
                            <strong>${formatCreditPackPrice(activePlan?.monthlyTotal ?? 0)}/mo</strong>
                          </p>
                        )}
                        {plan.note && !isActivePlan && (
                          <p className='msp-plan-note'>{plan.note}</p>
                        )}
                      </div>
                    ),
                  };
                });
                const focused =
                  planViews.find((v) => v.id === focusPlanId) ?? planViews[0];
                return (
                  <>
                    <div className='msp-plan-tiles' role='radiogroup'>
                      {planViews.map((v) => v.tile)}
                    </div>
                    {focused.controls}
                  </>
                );
              })()}
            </div>
          </div>

          <div
            className='msp-step'
            id='put-bite-to-work'
            style={{ scrollMarginTop: 120 }}
          >
            <div className='msp-step-head'>
              <span className='msp-step-num'>2</span>
              <div>
                <h3 className='msp-step-title'>Put BITE to work</h3>
                <p className='msp-step-sub'>
                  Work — Credits are used when BITE runs agentic workflows or
                  the Bulk Tariff Analyzer.
                </p>
              </div>
            </div>
            <div className='msp-step-body'>
              <div className='msp-credit-uses'>
                <div className='msp-credit-use'>
                  <div className='msp-credit-use-name'>Agentic workflows</div>
                  <div className='msp-credit-use-unit'>
                    credits per completed run
                  </div>
                  <ul className='msp-credit-use-list'>
                    <li>
                      <span>Import</span>
                      <strong>4 Credits</strong>
                    </li>
                    <li>
                      <span>Export</span>
                      <strong>10 Credits</strong>
                    </li>
                    <li>
                      <span>Design Assist</span>
                      <strong>10 Credits</strong>
                    </li>
                    <li>
                      <span>Beneficial Ownership</span>
                      <strong>10 Credits</strong>
                    </li>
                    <li>
                      <span>Export and Transshipment Risk</span>
                      <strong>5 Credits</strong>
                    </li>
                  </ul>
                </div>
                <div className='msp-credit-use'>
                  <div className='msp-credit-use-name'>Analyzers</div>
                  <div className='msp-credit-use-unit'>
                    credits per analyzer run
                  </div>
                  <ul className='msp-credit-use-list'>
                    <li>
                      <span>ACE Analyzer</span>
                      <strong>1 Credit/Entry</strong>
                    </li>
                    <li>
                      <span>AES Analyzer</span>
                      <strong>1 Credit/Shipment</strong>
                    </li>
                    <li>
                      <span>Bulk Tariff Analyzer</span>
                      <strong>1 Credit/5 Rows</strong>
                    </li>
                  </ul>
                </div>
              </div>
              <div className='msp-step-label'>
                <span className='msp-step-muted'> </span>
              </div>
              <div className='msp-credit-card msp-analyzers-section'>
                <div className='msp-credit-header'>
                  <div className='msp-credit-icon'>
                    <IconTarget />
                  </div>
                  <div className='msp-credit-header-text'>
                    <div className='msp-credit-title-row'>
                      <div className='msp-credit-title'>Add Credits</div>
                      <span className='msp-pill msp-pill-onetime'>
                        One-time purchase
                      </span>
                      {activeAnalyzersCreditPack && (
                        <span className='msp-inline-active-badge'>Active</span>
                      )}
                    </div>
                    <div className='msp-credit-subtitle'>
                      Shared by agentic workflows and analyzers — credits never
                      expire
                    </div>
                    {analyzersBalance && analyzersBalance.total > 0 && (
                      <div className='msp-credit-subtitle'>
                        Current balance:{' '}
                        {(
                          analyzersBalance.total - analyzersBalance.used
                        ).toLocaleString()}{' '}
                        of {analyzersBalance.total.toLocaleString()} credits
                        remaining
                      </div>
                    )}
                  </div>
                  {analyzersCreditSelection.selectedCredits && (
                    <button
                      className='icon-btn-round'
                      onClick={() =>
                        setAnalyzersCreditSelection({ selectedCredits: '' })
                      }
                      style={{ marginLeft: 'auto' }}
                    >
                      <IconX />
                    </button>
                  )}
                </div>
                <div className='msp-credit-body'>
                  <select
                    className='msp-credit-select msp-credit-select-full'
                    value={analyzersCreditSelection.selectedCredits}
                    onChange={(e) =>
                      setAnalyzersCreditSelection({
                        selectedCredits: e.target.value,
                      })
                    }
                  >
                    <option value=''>Select credits (one-time purchase)</option>
                    {analyzersCreditPacks.map((p) => (
                      <option key={p.credits} value={String(p.credits)}>
                        {p.credits.toLocaleString()} credits — $
                        {formatCreditPackPrice(p.price)} one-time
                      </option>
                    ))}
                  </select>
                  <div className='msp-credit-subtotal-row'>
                    {selectedAnalyzersPack && (
                      <span className='msp-section-subtotal'>
                        {blockTrialOneTimeAnalyzersPurchase
                          ? 'Select Pro or Business to enable this purchase during free trial.'
                          : `+$${formatCreditPackPrice(
                              selectedAnalyzersPack.price,
                            )}`}{' '}
                        {!blockTrialOneTimeAnalyzersPurchase && (
                          <span className='msp-section-subtotal-period'>
                            once
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='msp-step'>
            <div className='msp-step-head'>
              <span className='msp-step-num'>3</span>
              <div>
                <h3 className='msp-step-title'>Monitor</h3>
                <p className='msp-step-sub'>
                  Store — monthly capacity for your product and entity
                  databases.
                </p>
              </div>
            </div>
            <div className='msp-step-body'>
              <div className='msp-credit-card msp-database-section'>
                <div className='msp-credit-header'>
                  <div className='msp-credit-icon msp-credit-icon-db'>
                    <IconDatabase />
                  </div>
                  <div className='msp-credit-header-text'>
                    <div className='msp-credit-title-row'>
                      <div className='msp-credit-title'>Database Records</div>
                      <span className='msp-pill msp-pill-monthly'>Monthly</span>
                      {hasAnyActiveDatabasePack && (
                        <span className='msp-inline-active-badge'>Active</span>
                      )}
                    </div>
                    <div className='msp-credit-subtitle'>
                      Recurring storage for your entities and products (1 record
                      = 1 entity or SKU)
                    </div>
                    {hasAnyActiveDatabasePack && (
                      <div className='msp-credit-subtitle'>
                        Current plan:{' '}
                        {combinedActiveDatabaseCredits.toLocaleString()} rows
                        — $
                        {formatCreditPackPrice(
                          combinedActiveDatabaseMonthlyTotal,
                        )}
                        /mo
                      </div>
                    )}
                    {hasBothLegacyDatabaseSubscriptions && (
                      <div
                        className='msp-credit-subtitle'
                        style={{ color: 'var(--warning)' }}
                      >
                        Picking a new tier below and completing checkout merges
                        your Entity and Product Database plans into one combined
                        subscription, prorated for today.
                      </div>
                    )}
                  </div>
                  {databaseCreditSelection.selectedCredits && (
                    <button
                      className='icon-btn-round'
                      onClick={() =>
                        setDatabaseCreditSelection({ selectedCredits: '' })
                      }
                      style={{ marginLeft: 'auto' }}
                    >
                      <IconX />
                    </button>
                  )}
                </div>
                <div className='msp-credit-body'>
                  <select
                    className='msp-credit-select msp-credit-select-full'
                    value={databaseSelectValue}
                    onChange={(e) =>
                      setDatabaseCreditSelection({
                        selectedCredits: e.target.value,
                      })
                    }
                  >
                    <option value=''>Select database rows</option>
                    {databaseTierOptions.map((p) => (
                      <option key={p.credits} value={String(p.credits)}>
                        {p.credits.toLocaleString()} rows — $
                        {formatCreditPackPrice(p.price)}/mo
                        {roundedCombinedDatabasePack?.credits === p.credits
                          ? hasBothLegacyDatabaseSubscriptions
                            ? ' (≈ your combined total, rounded up)'
                            : ' (Current plan)'
                          : ''}
                      </option>
                    ))}
                  </select>
                  {databaseNeedsBusinessLicense && (
                    <div
                      className='msp-credit-subtitle'
                      style={{ color: 'var(--warning)' }}
                    >
                      Database Records require at least one Business license.
                      Add a Business license or clear this selection to check
                      out.
                    </div>
                  )}
                  <div className='msp-credit-subtotal-row'>
                    {displayedDatabasePack && (
                      <span className='msp-section-subtotal'>
                        +$
                        {formatCreditPackPrice(
                          displayedDatabasePack.price +
                            databaseAddOnsMonthlyPrice,
                        )}{' '}
                        <span className='msp-section-subtotal-period'>/mo</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <aside className='msp-builder-aside'>
          {/* Always-present scroll anchor — the panel below only mounts once
              hasSelection/licenseManager.hasPendingChanges is true, so a ref on
              the conditional element itself is null right after a selection is
              made (before the re-render that mounts it), and scrollIntoView
              silently no-ops. Anchoring on this permanent div fixes that. */}
          <div ref={orderSummaryRef} />
          {canManageBilling &&
            (hasSelection || licenseManager.hasPendingChanges) && (
              <div className='msp-order-wrap'>
                <div className='order-summary'>
                  <div className='msp-order-header'>
                    <div className='msp-order-icon'>
                      <IconShoppingCart />
                    </div>
                    <div>
                      <div className='msp-order-title'>Plan summary</div>
                      <div className='msp-order-subtitle'>
                        {totalLicenses > 0
                          ? `${totalLicenses} ${
                              totalLicenses === 1 ? 'license' : 'licenses'
                            } selected`
                          : licenseManager.hasPendingChanges
                          ? 'Pending license changes'
                          : 'Review your changes below'}
                      </div>
                    </div>
                    <button
                      className='pricing-btn pricing-btn-outline'
                      style={{ marginLeft: 'auto' }}
                      onClick={() => {
                        resetSelections();
                        licenseManager.revert();
                      }}
                    >
                      Revert changes
                    </button>
                  </div>
                  <div className='msp-order-items'>
                    {licenseManager.changes.map((c, i) => (
                      <div
                        key={`license-change-${i}`}
                        className='msp-order-line'
                      >
                        <div className='msp-order-line-left'>
                          <span className='msp-order-name'>{c.label}</span>
                          <span className='msp-order-qty'>{c.detail}</span>
                        </div>
                        {c.amount && (
                          <span className='msp-order-price'>{c.amount}</span>
                        )}
                      </div>
                    ))}
                    {plans.map((plan) => {
                      const qty = quantities[plan.id] || 0;
                      if (qty === 0) return null;
                      return (
                        <div key={plan.id} className='msp-order-line'>
                          <div className='msp-order-line-left'>
                            <button
                              className='msp-order-remove'
                              disabled={!canManageBilling}
                              onClick={() =>
                                setQuantities((prev) => ({
                                  ...prev,
                                  [plan.id]: 0,
                                }))
                              }
                            >
                              <IconX />
                            </button>
                            <span
                              className='msp-order-dot'
                              style={{ background: plan.accent }}
                            />
                            <span className='msp-order-name'>{plan.name}</span>
                            <span className='msp-order-qty'>
                              × {qty} {qty === 1 ? 'user' : 'users'}
                            </span>
                          </div>
                          <span className='msp-order-price'>
                            ${plan.price * qty}/mo
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {totalCredits > 0 && (
                    <div className='msp-included-credits'>
                      <div className='msp-included-credits-left'>
                        <IconZap />
                        <span className='msp-included-credits-label'>
                          Analyze & Work credits included
                        </span>
                      </div>
                      <span className='msp-included-credits-value'>
                        {totalCredits.toLocaleString()} credits
                      </span>
                    </div>
                  )}
                  {selectedDatabasePack && (
                    <div className='msp-pack-line'>
                      <div className='msp-pack-line-left'>
                        {!activeDatabaseCreditPack && (
                          <button
                            className='msp-order-remove'
                            disabled={!canManageBilling}
                            onClick={() => {
                              setDatabaseCreditSelection({
                                selectedCredits: '',
                              });
                            }}
                          >
                            <IconX />
                          </button>
                        )}
                        <IconDatabase />
                        <span className='msp-pack-line-label'>
                          Database record pack
                        </span>
                        <span className='msp-pack-line-detail'>
                          ({selectedDatabasePack.credits.toLocaleString()}{' '}
                          records)
                        </span>
                      </div>
                      <span className='msp-pack-line-price'>
                        +${formatCreditPackPrice(selectedDatabasePack.price)}/mo
                      </span>
                    </div>
                  )}
                  {selectedDatabaseAddOns.map((addOn) => (
                    <div key={addOn.id} className='msp-pack-line'>
                      <div className='msp-pack-line-left'>
                        <button
                          className='msp-order-remove'
                          disabled={!canManageBilling}
                          onClick={() =>
                            setDatabaseAddOnSelection((prev) => ({
                              ...prev,
                              [addOn.id]: false,
                            }))
                          }
                        >
                          <IconX />
                        </button>
                        <IconDatabase />
                        <span className='msp-pack-line-label'>
                          {addOn.label}
                        </span>
                        {!addOn.usageBased && (
                          <span className='msp-pack-line-detail'>
                            ({addOnCreditBasis.toLocaleString()} credits × $
                            {addOn.perCredit.toFixed(2)})
                          </span>
                        )}
                      </div>
                      <span className='msp-pack-line-price'>
                        {addOn.usageBased
                          ? `$${addOn.perEntity.toFixed(2)} / ${
                              addOn.id === 'corporateOwnership'
                                ? 'ownership check'
                                : 'entity verification'
                            }`
                          : `+$${formatCreditPackPrice(
                              addOn.perCredit * addOnCreditBasis,
                            )}/mo`}
                      </span>
                    </div>
                  ))}
                  {checkoutAnalyzersPack && (
                    <div className='msp-pack-line'>
                      <div className='msp-pack-line-left'>
                        <IconTarget />
                        <span className='msp-pack-line-label'>
                          Analyze & Work credit pack
                        </span>
                        <span className='msp-pack-line-detail'>
                          ({checkoutAnalyzersPack.credits.toLocaleString()}{' '}
                          credits)
                        </span>
                      </div>
                      <span className='msp-pack-line-price'>
                        +${formatCreditPackPrice(checkoutAnalyzersPack.price)}{' '}
                        once
                      </span>
                    </div>
                  )}
                  {checkoutWorkflowsPack && (
                    <div className='msp-pack-line'>
                      <div className='msp-pack-line-left'>
                        <button
                          className='msp-order-remove'
                          disabled={!canManageBilling}
                          onClick={() => {
                            setWorkflowsCreditSelection({
                              selectedCredits: '',
                            });
                          }}
                        >
                          <IconX />
                        </button>
                        <IconSparkles />
                        <span className='msp-pack-line-label'>
                          Workflows credit pack
                        </span>
                        <span className='msp-pack-line-detail'>
                          ({checkoutWorkflowsPack.credits.toLocaleString()}{' '}
                          credits)
                        </span>
                      </div>
                      <span className='msp-pack-line-price'>
                        +${formatCreditPackPrice(checkoutWorkflowsPack.price)}
                        /mo
                      </span>
                    </div>
                  )}
                  {creditCalc.totalCredits > 0 && (
                    <div className='msp-calc-line'>
                      <div className='msp-calc-line-left'>
                        <button
                          className='msp-order-remove'
                          disabled={!canManageBilling}
                          onClick={() =>
                            setCreditSelections(initialCreditSelections)
                          }
                        >
                          <IconX />
                        </button>
                        <IconCalculator />
                        <span className='msp-calc-line-label'>
                          Estimated Credits from Calculator
                        </span>
                      </div>
                      <span className='msp-calc-line-value'>
                        {creditCalc.totalCredits.toLocaleString()} credits
                      </span>
                    </div>
                  )}
                  <div
                    className='msp-order-footer'
                    style={{
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: '1rem',
                    }}
                  >
                    <div className='msp-order-totals'>
                      {oneTimeTotal > 0 && (
                        <div>
                          <span className='msp-order-footer-label'>
                            One-time credits
                          </span>
                          <p
                            className='msp-order-grand-total'
                            style={{ fontSize: '1.375rem' }}
                          >
                            ${formatCreditPackPrice(oneTimeTotal)}
                          </p>
                        </div>
                      )}
                      <div>
                        <span className='msp-order-footer-label'>
                          Monthly total
                        </span>
                        <p
                          className='msp-order-grand-total'
                          style={{ fontSize: '1.375rem' }}
                        >
                          ${formatCreditPackPrice(combinedMonthlyTotal)}
                          <span
                            style={{
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              color: 'var(--muted-foreground, #9aa1ac)',
                            }}
                          >
                            /mo
                          </span>
                        </p>
                      </div>
                      <div>
                        <span className='msp-order-footer-label'>Due now</span>
                        <p className='msp-order-grand-total'>
                          ${formatCreditPackPrice(dueNow)}
                        </p>
                      </div>
                    </div>
                    {databaseNeedsBusinessLicense && (
                      <p
                        className='msp-credit-subtitle'
                        style={{ color: 'var(--warning)', margin: 0 }}
                      >
                        Database records require at least one Business license.
                        Add a Business license or remove the Database records to
                        continue.
                      </p>
                    )}
                    <button
                      className='msp-checkout-btn'
                      disabled={
                        !canManageBilling ||
                        databaseNeedsBusinessLicense ||
                        hasUnconfiguredCreditPack ||
                        isConfirmingLicenseChanges ||
                        (!licenseManager.hasPendingChanges &&
                          checkoutSubscriptions.length === 0)
                      }
                      onClick={async () => {
                        if (databaseNeedsBusinessLicense) {
                          return;
                        }
                        // Downgrades/removals (no proration owed) apply immediately,
                        // no payment step needed. Upgrades that owe a prorated
                        // amount today need the license Confirm modal's payment
                        // step first. Either way, any brand-new plan/credit
                        // selections still go through the existing Checkout modal —
                        // that flow (and the combined Database/Workflows monthly
                        // and Analyzers one-time price ids) is unchanged.
                        if (licenseManager.hasPendingChanges) {
                          if (
                            (licenseManager.quantityUpgradeProrationDueToday ??
                              0) > 0
                          ) {
                            // With packs also selected, CheckoutModal applies
                            // the license upgrade and buys the packs in one Pay.
                            if (hasSelection) {
                              setCheckoutOpen(true);
                              return;
                            }
                            setLicenseConfirmOpen(true);
                            return;
                          }
                          // Finish the license change before Checkout can start a
                          // second billing operation, and stop if it failed.
                          setIsConfirmingLicenseChanges(true);
                          let confirmed = false;
                          try {
                            confirmed = await licenseManager.confirmChanges();
                          } catch {
                            confirmed = false;
                          } finally {
                            setIsConfirmingLicenseChanges(false);
                          }
                          if (!confirmed) {
                            enqueueSnackbar(
                              'Your license changes could not be saved, so checkout was not started. Please try again.',
                              { variant: 'error' },
                            );
                            return;
                          }
                        }
                        if (hasSelection) {
                          setCheckoutOpen(true);
                        }
                      }}
                    >
                      Checkout <IconArrowRight />
                    </button>
                  </div>
                </div>
              </div>
            )}

          {!(
            canManageBilling &&
            (hasSelection || licenseManager.hasPendingChanges)
          ) && (
            <div className='order-summary msp-order-empty'>
              <div className='msp-order-title'>Your BITE estimate</div>
              <div className='msp-order-subtitle'>
                Choose a plan and users to see your monthly total.
              </div>
            </div>
          )}
        </aside>
      </div>

      <ConfirmModal
        open={licenseConfirmOpen}
        onClose={() => setLicenseConfirmOpen(false)}
        changes={licenseManager.changes}
        newTotal={licenseManager.newTotal}
        onConfirm={async (paymentMethodId?: string) => {
          const confirmed = await licenseManager.confirmChanges(
            paymentMethodId,
          );
          // Prorated license changes and new subscription/credit selections can
          // be made together; the handler stopped at this modal, so continue
          // into checkout for the remaining selections once it succeeds.
          if (confirmed && hasSelection) {
            setCheckoutOpen(true);
          }
          return confirmed;
        }}
        proratedDueToday={
          typeof licenseManager.quantityUpgradeProrationDueToday === 'number'
            ? licenseManager.quantityUpgradeProrationDueToday
            : null
        }
        requiresPayment={
          (licenseManager.quantityUpgradeProrationDueToday ?? 0) > 0
        }
        customerId={stripeCustomer?.id}
      />
      <CancelRenewModal
        open={licenseManager.cancelRenewModal.open}
        onClose={licenseManager.closeCancelRenewModal}
        onConfirm={licenseManager.confirmToggleCancel}
        type={licenseManager.cancelRenewModal.type}
        planName={licenseManager.cancelRenewModal.planName}
        renewsOn={licenseManager.cancelRenewModal.renewsOn}
      />

      <div
        className='plan-card msp-enterprise'
        style={{
          borderColor: `color-mix(in srgb, ${enterprisePlan.accent} 25%, transparent)`,
          marginBottom: '2rem',
        }}
      >
        <div className='msp-badge-wrap'>
          <span
            className='plan-badge'
            style={{ backgroundColor: enterprisePlan.accent }}
          >
            {enterprisePlan.badge}
          </span>
        </div>
        <div className='enterprise-inner'>
          <div className='enterprise-left'>
            <h3
              className='msp-plan-name'
              style={{ color: enterprisePlan.accent }}
            >
              {enterprisePlan.name}
            </h3>
            <p className='msp-ent-desc'>{enterprisePlan.description}</p>
            <div className='msp-ent-sales'>
              <h4 className='msp-ent-sales-title'>Talk to Sales</h4>
              <p className='msp-ent-sales-sub'>Custom pricing for your team</p>
            </div>
            <button
              className='msp-ent-cta'
              style={{ backgroundColor: enterprisePlan.accent }}
              disabled={!canManageBilling}
              onClick={() => (window.location.href = 'mailto:info@bitedata.io')}
            >
              Contact Sales <IconArrowRight />
            </button>
            <p className='msp-ent-email'>info@bitedata.io</p>
          </div>
          <div className='enterprise-right'>
            <div className='msp-ent-credits-box'>
              <div className='msp-info-row'>
                <IconSparkles />
                <span className='msp-info-label'>Custom credits</span>
              </div>
              <div className='msp-info-sub'>
                High volume discount pricing available
              </div>
            </div>
            <div className='msp-ent-includes-label'>Capabilities</div>
            <ul className='enterprise-features'>
              {enterprisePlan.capabilities.map((f, i) => (
                <li key={i} className='msp-feature-item'>
                  <span className='feature-check'>
                    <IconCheck />
                  </span>
                  <span className='msp-feature-text'>{f}</span>
                </li>
              ))}
            </ul>
            <div
              className='msp-ent-includes-label'
              style={{ marginTop: '0.75rem' }}
            >
              Outcomes
            </div>
            <ul className='enterprise-features'>
              {enterprisePlan.outcomes.map((f, i) => (
                <li key={i} className='msp-feature-item'>
                  <span className='feature-check'>
                    <IconTarget />
                  </span>
                  <span className='msp-feature-text'>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <CreditFaq />

      {/* Calculator Modal */}
      {calculatorOpen && (
        <div
          className='pricing-overlay'
          onClick={() => setCalculatorOpen(false)}
        >
          <div className='msp-calc-modal' onClick={(e) => e.stopPropagation()}>
            <CreditCalculator
              selections={creditSelections}
              onSelectionsChange={setCreditSelections}
              onClose={() => setCalculatorOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Floating Checkout Button */}
      {totalLicenses > 0 && (
        <div className='msp-floating-checkout'>
          <div className='msp-floating-checkout-inner'>
            <div className='msp-floating-checkout-info'>
              <span className='msp-floating-checkout-summary'>
                {totalLicenses} {totalLicenses === 1 ? 'license' : 'licenses'} ·{' '}
                <strong>${formatCreditPackPrice(monthlyGrandTotal)}/mo</strong>
              </span>
            </div>
            <button
              className='msp-floating-checkout-btn'
              disabled={!canManageBilling}
              onClick={() =>
                orderSummaryRef.current?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                })
              }
            >
              Go to Checkout <IconArrowRight />
            </button>
          </div>
        </div>
      )}

      <CheckoutModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        onPurchaseCompleted={async () => {
          // The buyer's seat is assigned by the invoice.paid webhook, not from
          // here: assigning it from the browser was refused for about a minute
          // after purchase, because the seat check reads Stripe's search
          // index, which lags new subscriptions.
          const purchasedLicense = checkoutSubscriptions.find(
            (subscription) => subscription.autoAssignLicense,
          )?.autoAssignLicense;
          await cancelTrialSubscriptions();
          await refreshBillingUI();
          if (purchasedLicense) {
            await waitForPurchasedLicense(purchasedLicense);
          }
          resetSelections();
        }}
        orderItems={plans
          .filter((p) => (quantities[p.id] || 0) > 0)
          .map((p) => ({
            name: p.name,
            accent: p.accent,
            qty: quantities[p.id],
            price: p.price,
          }))}
        totalCredits={totalCredits}
        creditPacks={[
          ...(selectedDatabasePack
            ? [
                {
                  type: 'database' as const,
                  credits: selectedDatabasePack.credits,
                  price: selectedDatabasePack.price,
                },
              ]
            : []),
          ...selectedDatabaseAddOns.map((addOn) => ({
            type: addOn.type,
            credits: addOnCreditBasis,
            price: addOn.usageBased ? 0 : addOn.perCredit * addOnCreditBasis,
          })),
          ...(checkoutAnalyzersPack
            ? [
                {
                  type: 'analyzers' as const,
                  credits: checkoutAnalyzersPack.credits,
                  price: checkoutAnalyzersPack.price,
                },
              ]
            : []),
          ...(checkoutWorkflowsPack
            ? [
                {
                  type: 'workflows' as const,
                  credits: checkoutWorkflowsPack.credits,
                  price: checkoutWorkflowsPack.price,
                },
              ]
            : []),
        ]}
        monthlyTotal={checkoutMonthlyTotal}
        tierChangeBilling={tierChangeBilling ?? undefined}
        oneTimeTotal={oneTimeTotal}
        subscriptions={checkoutSubscriptions}
        handleTabChange={handleTabChange}
        licenseUpgrade={
          licenseManager.hasPendingChanges &&
          (licenseManager.quantityUpgradeProrationDueToday ?? 0) > 0
            ? {
                dueToday: licenseManager.quantityUpgradeProrationDueToday ?? 0,
                apply: (paymentMethodId: string) =>
                  licenseManager.confirmChanges(paymentMethodId),
              }
            : undefined
        }
      />
    </div>
  );
};

export default SubscriptionPricing;
